(() => {
  const script = document.currentScript;
  const dictionaryUrl = script?.dataset.dictionaryUrl;
  if (!dictionaryUrl) return;

  const rootsSelector = '#itoris_dynamicproductoptions, .dpo-container, .cart-item__details dl .product-option, .pooltime-sticky-show-options, #nv-app';
  const excluded = 'script, style, textarea, input, [contenteditable="true"]';
  const textRecords = new WeakMap();
  const attributeRecords = new WeakMap();
  const pending = new Set();
  const observedRoots = new WeakSet();
  let dictionary;
  let dictionaryPromise;
  let scheduled = false;

  const normalize = value => value.replace(/\s+/g, ' ').trim();
  const priceSuffix = /\s*\(?[+−–-]\s*\d[\d\s.,]*(?:\s*(?:kr\.?|SEK|DKK|NOK|EUR|GBP|HUF|USD|%|€|£|Ft))\)?\s*$/i;

  function translate(value) {
    const normalized = normalize(value);
    if (!normalized) return value;
    const informationPrefix = 'Visa information om ';
    if (normalized.startsWith(informationPrefix) && dictionary['Visa information om']) {
      return dictionary['Visa information om'] + ' ' + translate(normalized.slice(informationPrefix.length));
    }
    const suffix = normalized.match(priceSuffix)?.[0] || '';
    const base = suffix ? normalized.slice(0, -suffix.length).trim() : normalized;
    let translated = dictionary[base];
    if (!translated && /[:*]$/.test(base)) {
      const label = base.replace(/\s*[:*]+$/, '');
      if (dictionary[label]) translated = dictionary[label] + base.slice(label.length);
    }
    if (!translated || translated === base) return value;
    const leading = value.match(/^\s*/)[0];
    const trailing = value.match(/\s*$/)[0];
    return leading + translated + suffix + trailing;
  }

  function translateText(node) {
    if (!node.parentElement || node.parentElement.closest(excluded)) return;
    const value = node.nodeValue;
    const previous = textRecords.get(node);
    if (previous?.translated === value) return;
    const translated = translate(value);
    textRecords.set(node, { source: value, translated });
    if (translated !== value) node.nodeValue = translated;
  }

  function translateAttributes(element) {
    if (element.closest('script, style, [contenteditable="true"]')) return;
    const previous = attributeRecords.get(element) || {};
    for (const name of ['title', 'alt', 'aria-label', 'placeholder']) {
      const value = element.getAttribute(name);
      if (value === null || previous[name]?.translated === value) continue;
      const translated = translate(value);
      previous[name] = { source: value, translated };
      if (translated !== value) element.setAttribute(name, translated);
    }
    attributeRecords.set(element, previous);
  }

  function apply(root) {
    if (!root.isConnected) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) translateText(node);
    translateAttributes(root);
    root.querySelectorAll('[title], [alt], [aria-label], [placeholder]').forEach(translateAttributes);
    root.dataset.pooltimeOptionsLocale = script.dataset.locale;
  }

  async function flush() {
    scheduled = false;
    try {
      if (!dictionaryPromise) {
        dictionaryPromise = fetch(dictionaryUrl, { credentials: 'omit' })
          .then(response => {
            if (!response.ok) throw new Error('Dictionary unavailable');
            return response.json();
          })
          .then(data => data.translations);
      }
      dictionary = await dictionaryPromise;
      if (!dictionary || typeof dictionary !== 'object') return;
      const roots = [...pending];
      pending.clear();
      roots.forEach(apply);
    } catch {
      pending.clear();
    }
  }

  function enqueue(node) {
    const element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
    if (!element) return;
    const parent = element.closest(rootsSelector);
    if (parent) watch(parent);
    element.querySelectorAll(rootsSelector).forEach(watch);
    if (pending.size && !scheduled) {
      scheduled = true;
      requestAnimationFrame(flush);
    }
  }

  const observer = new MutationObserver(records => {
    for (const record of records) {
      if (record.type === 'characterData' || record.type === 'attributes') enqueue(record.target);
      else record.addedNodes.forEach(enqueue);
    }
  });
  function watch(root) {
    pending.add(root);
    if (observedRoots.has(root)) return;
    observedRoots.add(root);
    observer.observe(root, { childList: true, characterData: true, attributes: true, attributeFilter: ['title', 'alt', 'aria-label', 'placeholder'], subtree: true });
  }

  new MutationObserver(records => {
    for (const record of records) record.addedNodes.forEach(enqueue);
  }).observe(document.body, { childList: true, subtree: true });
  enqueue(document.body);
})();

